import { useEffect, useState } from "react";
import { Button, Spin, Table } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

import { ordersApi } from "../api/orders";
import type { DashboardSummary, Order } from "../api/types";
import { Pill } from "../components/Pill";
import { formatINR } from "../utils/format";
import { useAuthStore } from "../store/auth";

function Kpi({
  label,
  value,
  delta,
  up,
}: {
  label: string;
  value: string;
  delta?: string;
  up?: boolean;
}) {
  return (
    <div className="card card-pad">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {delta && (
        <div
          style={{
            fontSize: 12,
            color: up ? "var(--success)" : "var(--text-secondary)",
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {delta}
        </div>
      )}
    </div>
  );
}

export function Dashboard() {
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  useEffect(() => {
    ordersApi
      .dashboard()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="page-shell" style={{ display: "flex", justifyContent: "center" }}>
        <Spin />
      </div>
    );
  }

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const todayDelta =
    data.yesterday_orders > 0
      ? Math.round(((data.today_orders - data.yesterday_orders) / data.yesterday_orders) * 100)
      : 0;

  const maxShop = Math.max(...data.top_shops.map((s) => Number(s.value)), 1);

  const columns = [
    {
      title: "Order #",
      dataIndex: "order_number",
      render: (v: string) => <span className="mono" style={{ fontSize: 12 }}>{v}</span>,
    },
    { title: "Employee", dataIndex: "employee_name" },
    { title: "Shop", dataIndex: "shop_name" },
    {
      title: "Amount",
      dataIndex: "total_amount",
      align: "right" as const,
      render: (v: string) => <span className="tabular">{formatINR(v)}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v: Order["status"]) => <Pill kind={v} />,
    },
    {
      title: "",
      render: (_: unknown, row: Order) => (
        <Button
          size="small"
          type={row.status === "pending" ? "primary" : "default"}
          onClick={() => navigate(`/orders?review=${row.id}`)}
        >
          {row.status === "pending" ? "Review" : "View"}
        </Button>
      ),
    },
  ];

  return (
    <div className="page-shell">
      <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
        {greeting()}, {user?.full_name?.split(" ")[0]}
      </h1>
      <div style={{ fontSize: 14, color: "var(--text-secondary)", marginTop: 4 }}>
        Here's what's happening today
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginTop: 24 }}>
        <Kpi label="Pending Orders" value={String(data.pending_orders)} delta={`${data.pending_orders} awaiting review`} up />
        <Kpi
          label="Today's Orders"
          value={String(data.today_orders)}
          delta={`${todayDelta >= 0 ? "+" : ""}${todayDelta}% vs yesterday`}
          up={todayDelta >= 0}
        />
        <Kpi
          label="Active Employees"
          value={String(data.active_employees)}
          delta={`${data.inactive_employees} inactive`}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 24, marginTop: 48 }}>
        <div className="card card-pad">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 500 }}>Recent Incoming Orders</div>
            <Link to="/orders" style={{ fontSize: 13 }}>View all →</Link>
          </div>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            columns={columns}
            dataSource={data.recent_orders.slice(0, 5)}
          />
        </div>

        <div className="card card-pad">
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 12 }}>Top 5 Shops This Month</div>
          {data.top_shops.map((s, i) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", fontSize: 13 }}>
              <div style={{ width: 16, color: "var(--text-muted)" }}>{i + 1}</div>
              <div style={{ flex: 1, position: "relative", height: 20 }}>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "rgba(37,99,235,0.1)",
                    width: `${(Number(s.value) / maxShop) * 100}%`,
                    borderRadius: 3,
                  }}
                />
                <div style={{ position: "relative", padding: "2px 6px", display: "flex", justifyContent: "space-between" }}>
                  <span>{s.name}</span>
                  <span className="tabular">{formatINR(s.value)}</span>
                </div>
              </div>
            </div>
          ))}
          {data.top_shops.length === 0 && <div className="small-muted">No data yet.</div>}
        </div>
      </div>
    </div>
  );
}
